<script>
import { mapActions, mapState } from 'vuex';
import {
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BellOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  HistoryOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import bem from '@/utils/bem';
import PageHeader from '@/components/ux/pageHeader/page-header.vue';

const b = bem('alerts-page');

export default {
  components: {
    PageHeader,
    ReloadOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    BellOutlined,
    CheckCircleOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    HistoryOutlined,
  },
  data() {
    return {
      title: 'Alerts',
      activeTab: 'rules',
      ruleModalVisible: false,
      ruleModalMode: 'create',
      editingRule: null,
      ruleForm: {
        name: '',
        description: '',
        conditionType: 'error_count',
        conditionThreshold: 10,
        conditionIntervalMinutes: 5,
        filters: {
          pagePath: '',
          errorType: '',
          browser: '',
          endpoint: '',
        },
        severity: 'warning',
        notifyEmail: [],
        cooldownMinutes: 60,
        isActive: true,
      },
      historyPagination: {
        page: 1,
        limit: 20,
      },
      conditionTypeOptions: [
        { value: 'error_count', label: 'Error Count', description: 'Trigger when error count exceeds threshold' },
        { value: 'error_rate', label: 'Error Rate', description: 'Trigger when error rate (% of sessions) exceeds threshold' },
        { value: 'api_error', label: 'API Error', description: 'Trigger on specific API endpoint errors' },
        { value: 'js_error', label: 'JS Error', description: 'Trigger on specific JavaScript error type' },
      ],
      severityOptions: [
        { value: 'info', label: 'Info', color: 'blue' },
        { value: 'warning', label: 'Warning', color: 'orange' },
        { value: 'critical', label: 'Critical', color: 'red' },
      ],
      errorTypeOptions: [
        { value: '', label: 'All Types' },
        { value: 'TypeError', label: 'TypeError' },
        { value: 'ReferenceError', label: 'ReferenceError' },
        { value: 'SyntaxError', label: 'SyntaxError' },
        { value: 'Error', label: 'Error' },
        { value: 'UnhandledRejection', label: 'Unhandled Rejection' },
      ],
      historyColumns: [
        {
          title: 'Time',
          dataIndex: 'triggeredAt',
          key: 'triggeredAt',
          width: 180,
        },
        {
          title: 'Rule',
          key: 'rule',
        },
        {
          title: 'Value',
          dataIndex: 'triggeredValue',
          key: 'triggeredValue',
          width: 100,
        },
        {
          title: 'Sessions',
          key: 'sessions',
          width: 100,
        },
        {
          title: 'Email Sent',
          dataIndex: 'emailSent',
          key: 'emailSent',
          width: 100,
        },
        {
          title: 'Actions',
          key: 'actions',
          width: 120,
        },
      ],
    };
  },
  computed: {
    ...mapState({
      rules: (state) => state.analytics.alertRules,
      rulesLoading: (state) => state.analytics.alertRulesLoading,
      history: (state) => state.analytics.alertHistory,
      historyTotal: (state) => state.analytics.alertHistoryTotal,
      historyLoading: (state) => state.analytics.alertHistoryLoading,
      creatingRule: (state) => state.analytics.creatingAlertRule,
      updatingRule: (state) => state.analytics.updatingAlertRule,
      deletingRule: (state) => state.analytics.deletingAlertRule,
    }),
    historyPaginationConfig() {
      return {
        current: this.historyPagination.page,
        pageSize: this.historyPagination.limit,
        total: this.historyTotal,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} alerts`,
      };
    },
    ruleModalTitle() {
      return this.ruleModalMode === 'create' ? 'Create Alert Rule' : 'Edit Alert Rule';
    },
    conditionDescription() {
      const { conditionType, conditionThreshold, conditionIntervalMinutes } = this.ruleForm;
      switch (conditionType) {
        case 'error_count':
          return `Trigger when more than ${conditionThreshold} errors occur in ${conditionIntervalMinutes} minutes`;
        case 'error_rate':
          return `Trigger when error rate exceeds ${conditionThreshold}% of sessions in ${conditionIntervalMinutes} minutes`;
        case 'api_error':
          return `Trigger when more than ${conditionThreshold} API errors occur in ${conditionIntervalMinutes} minutes`;
        case 'js_error':
          return `Trigger when more than ${conditionThreshold} JS errors occur in ${conditionIntervalMinutes} minutes`;
        default:
          return '';
      }
    },
  },
  methods: {
    b,
    ...mapActions('analytics', [
      'getAlertRules',
      'createAlertRule',
      'updateAlertRule',
      'deleteAlertRule',
      'getAlertHistory',
    ]),
    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).format('MMM DD, YYYY HH:mm:ss');
    },
    formatRelativeTime(date) {
      if (!date) return 'Never';
      return dayjs(date).fromNow();
    },
    getSeverityColor(severity) {
      const option = this.severityOptions.find((s) => s.value === severity);
      return option?.color || 'default';
    },
    getSeverityLabel(severity) {
      const option = this.severityOptions.find((s) => s.value === severity);
      return option?.label || severity;
    },
    loadRules() {
      this.getAlertRules();
    },
    loadHistory() {
      this.getAlertHistory({
        page: this.historyPagination.page,
        limit: this.historyPagination.limit,
      });
    },
    onTabChange(key) {
      if (key === 'history') {
        this.loadHistory();
      }
    },
    onHistoryTableChange(pagination) {
      this.historyPagination.page = pagination.current;
      this.historyPagination.limit = pagination.pageSize;
      this.loadHistory();
    },
    openCreateModal() {
      this.ruleModalMode = 'create';
      this.editingRule = null;
      this.resetRuleForm();
      this.ruleModalVisible = true;
    },
    openEditModal(rule) {
      this.ruleModalMode = 'edit';
      this.editingRule = rule;
      this.ruleForm = {
        name: rule.name,
        description: rule.description || '',
        conditionType: rule.conditionType,
        conditionThreshold: rule.conditionThreshold,
        conditionIntervalMinutes: rule.conditionIntervalMinutes,
        filters: {
          pagePath: rule.filters?.pagePath || '',
          errorType: rule.filters?.errorType || '',
          browser: rule.filters?.browser || '',
          endpoint: rule.filters?.endpoint || '',
        },
        severity: rule.severity,
        notifyEmail: rule.notifyEmail || [],
        cooldownMinutes: rule.cooldownMinutes,
        isActive: rule.isActive,
      };
      this.ruleModalVisible = true;
    },
    resetRuleForm() {
      this.ruleForm = {
        name: '',
        description: '',
        conditionType: 'error_count',
        conditionThreshold: 10,
        conditionIntervalMinutes: 5,
        filters: {
          pagePath: '',
          errorType: '',
          browser: '',
          endpoint: '',
        },
        severity: 'warning',
        notifyEmail: [],
        cooldownMinutes: 60,
        isActive: true,
      };
    },
    closeRuleModal() {
      this.ruleModalVisible = false;
      this.editingRule = null;
      this.resetRuleForm();
    },
    async saveRule() {
      const payload = {
        ...this.ruleForm,
        filters: Object.fromEntries(
          Object.entries(this.ruleForm.filters).filter(([, v]) => v),
        ),
      };

      if (this.ruleModalMode === 'create') {
        await this.createAlertRule(payload);
      } else {
        await this.updateAlertRule({ id: this.editingRule.id, ...payload });
      }

      this.closeRuleModal();
      this.loadRules();
    },
    async toggleRuleActive(rule) {
      await this.updateAlertRule({
        id: rule.id,
        isActive: !rule.isActive,
      });
      this.loadRules();
    },
    async confirmDeleteRule(rule) {
      await this.deleteAlertRule(rule.id);
      this.loadRules();
    },
    viewSession(sessionId) {
      if (!sessionId) return;
      this.$router.push(`/analytics/sessions/${sessionId}`);
    },
    getRuleName(ruleId) {
      const rule = this.rules.find((r) => r.id === ruleId);
      return rule?.name || `Rule #${ruleId}`;
    },
  },
  mounted() {
    this.loadRules();
  },
};
</script>

<template>
  <div :class="b()">
    <page-header :title="title">
      <template #actions>
        <a-space>
          <a-button type="primary" @click="openCreateModal">
            <plus-outlined /> Create Rule
          </a-button>
          <a-button @click="loadRules">
            <reload-outlined /> Refresh
          </a-button>
        </a-space>
      </template>
    </page-header>

    <a-tabs v-model:activeKey="activeTab" @change="onTabChange">
      <!-- Rules Tab -->
      <a-tab-pane key="rules">
        <template #tab>
          <bell-outlined /> Alert Rules
        </template>

        <a-spin :spinning="rulesLoading">
          <div v-if="!rules.length" :class="b('empty')">
            <a-empty description="No alert rules configured">
              <a-button type="primary" @click="openCreateModal">
                <plus-outlined /> Create First Rule
              </a-button>
            </a-empty>
          </div>

          <div v-else :class="b('rules-grid')">
            <a-card
              v-for="rule in rules"
              :key="rule.id"
              :class="[b('rule-card'), { inactive: !rule.isActive }]"
            >
              <template #title>
                <div :class="b('rule-header')">
                  <a-tag :color="getSeverityColor(rule.severity)">
                    {{ getSeverityLabel(rule.severity) }}
                  </a-tag>
                  <span :class="b('rule-name')">{{ rule.name }}</span>
                </div>
              </template>
              <template #extra>
                <a-space>
                  <a-tooltip :title="rule.isActive ? 'Pause' : 'Activate'">
                    <a-button
                      type="text"
                      size="small"
                      @click="toggleRuleActive(rule)"
                    >
                      <pause-circle-outlined v-if="rule.isActive" />
                      <play-circle-outlined v-else />
                    </a-button>
                  </a-tooltip>
                  <a-tooltip title="Edit">
                    <a-button type="text" size="small" @click="openEditModal(rule)">
                      <edit-outlined />
                    </a-button>
                  </a-tooltip>
                  <a-popconfirm
                    title="Are you sure you want to delete this rule?"
                    ok-text="Yes"
                    cancel-text="No"
                    @confirm="confirmDeleteRule(rule)"
                  >
                    <a-tooltip title="Delete">
                      <a-button type="text" size="small" danger>
                        <delete-outlined />
                      </a-button>
                    </a-tooltip>
                  </a-popconfirm>
                </a-space>
              </template>

              <div :class="b('rule-content')">
                <p v-if="rule.description" :class="b('rule-description')">
                  {{ rule.description }}
                </p>

                <div :class="b('rule-condition')">
                  <strong>Condition:</strong>
                  {{ rule.conditionType }} &gt; {{ rule.conditionThreshold }}
                  in {{ rule.conditionIntervalMinutes }} min
                </div>

                <div v-if="Object.keys(rule.filters || {}).length" :class="b('rule-filters')">
                  <strong>Filters:</strong>
                  <a-tag v-for="(value, key) in rule.filters" :key="key" size="small">
                    {{ key }}: {{ value }}
                  </a-tag>
                </div>

                <div :class="b('rule-notify')">
                  <strong>Notify:</strong>
                  <span v-if="rule.notifyEmail?.length">
                    {{ rule.notifyEmail.join(', ') }}
                  </span>
                  <span v-else class="text-muted">No recipients</span>
                </div>

                <div :class="b('rule-footer')">
                  <span :class="b('rule-status')">
                    <check-circle-outlined v-if="rule.isActive" style="color: #52c41a" />
                    <pause-circle-outlined v-else style="color: #faad14" />
                    {{ rule.isActive ? 'Active' : 'Paused' }}
                  </span>
                  <span :class="b('rule-last-triggered')">
                    Last triggered: {{ formatRelativeTime(rule.lastTriggeredAt) }}
                  </span>
                </div>
              </div>
            </a-card>
          </div>
        </a-spin>
      </a-tab-pane>

      <!-- History Tab -->
      <a-tab-pane key="history">
        <template #tab>
          <history-outlined /> Alert History
        </template>

        <a-card>
          <a-table
            :dataSource="history"
            :columns="historyColumns"
            :loading="historyLoading"
            :pagination="historyPaginationConfig"
            row-key="id"
            @change="onHistoryTableChange"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'triggeredAt'">
                {{ formatDate(record.triggeredAt) }}
              </template>

              <template v-else-if="column.key === 'rule'">
                <div>
                  <div>{{ getRuleName(record.ruleId) }}</div>
                  <div v-if="record.context?.pagePath" class="text-small text-muted">
                    {{ record.context.pagePath }}
                  </div>
                </div>
              </template>

              <template v-else-if="column.key === 'sessions'">
                {{ record.context?.sessionsAffected || '-' }}
              </template>

              <template v-else-if="column.key === 'emailSent'">
                <a-tag v-if="record.emailSent" color="green">Sent</a-tag>
                <a-tag v-else color="default">Not Sent</a-tag>
              </template>

              <template v-else-if="column.key === 'actions'">
                <a-button
                  v-if="record.sessionId"
                  type="link"
                  size="small"
                  @click="viewSession(record.sessionId)"
                >
                  View Session
                </a-button>
              </template>
            </template>
          </a-table>
        </a-card>
      </a-tab-pane>
    </a-tabs>

    <!-- Create/Edit Rule Modal -->
    <a-modal
      v-model:visible="ruleModalVisible"
      :title="ruleModalTitle"
      width="700px"
      :confirm-loading="creatingRule || updatingRule"
      @ok="saveRule"
      @cancel="closeRuleModal"
    >
      <a-form layout="vertical">
        <a-form-item label="Name" required>
          <a-input v-model:value="ruleForm.name" placeholder="e.g., Checkout Page Errors" />
        </a-form-item>

        <a-form-item label="Description">
          <a-textarea
            v-model:value="ruleForm.description"
            placeholder="Describe what this alert monitors"
            :rows="2"
          />
        </a-form-item>

        <a-divider>Condition</a-divider>

        <a-form-item label="Condition Type">
          <a-select v-model:value="ruleForm.conditionType" style="width: 100%">
            <a-select-option
              v-for="opt in conditionTypeOptions"
              :key="opt.value"
              :value="opt.value"
            >
              <div>{{ opt.label }}</div>
              <div style="font-size: 12px; color: #999">{{ opt.description }}</div>
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="Threshold">
              <a-input-number
                v-model:value="ruleForm.conditionThreshold"
                :min="1"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Time Window (minutes)">
              <a-input-number
                v-model:value="ruleForm.conditionIntervalMinutes"
                :min="1"
                :max="1440"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-alert
          :message="conditionDescription"
          type="info"
          show-icon
          style="margin-bottom: 16px"
        />

        <a-divider>Filters (Optional)</a-divider>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="Page Path">
              <a-input
                v-model:value="ruleForm.filters.pagePath"
                placeholder="/checkout/*"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Error Type">
              <a-select
                v-model:value="ruleForm.filters.errorType"
                :options="errorTypeOptions"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="Browser">
              <a-input v-model:value="ruleForm.filters.browser" placeholder="Chrome" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="API Endpoint">
              <a-input
                v-model:value="ruleForm.filters.endpoint"
                placeholder="/api/auth/*"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-divider>Notification</a-divider>

        <a-form-item label="Severity">
          <a-radio-group v-model:value="ruleForm.severity">
            <a-radio-button
              v-for="opt in severityOptions"
              :key="opt.value"
              :value="opt.value"
            >
              <a-tag :color="opt.color" :bordered="false">{{ opt.label }}</a-tag>
            </a-radio-button>
          </a-radio-group>
        </a-form-item>

        <a-form-item label="Email Recipients">
          <a-select
            v-model:value="ruleForm.notifyEmail"
            mode="tags"
            placeholder="Enter email addresses"
            style="width: 100%"
          />
          <div style="font-size: 12px; color: #999; margin-top: 4px">
            Press Enter after each email address
          </div>
        </a-form-item>

        <a-form-item label="Cooldown (minutes)">
          <a-input-number
            v-model:value="ruleForm.cooldownMinutes"
            :min="1"
            :max="1440"
            style="width: 200px"
          />
          <span style="margin-left: 8px; color: #999">
            Prevent duplicate alerts within this period
          </span>
        </a-form-item>

        <a-form-item>
          <a-checkbox v-model:checked="ruleForm.isActive">
            Activate rule immediately
          </a-checkbox>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<style lang="scss">
.alerts-page {
  padding: 24px;

  &__empty {
    padding: 60px 0;
    text-align: center;
  }

  &__rules-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    gap: 16px;
  }

  &__rule-card {
    &.inactive {
      opacity: 0.7;
      background: #fafafa;
    }
  }

  &__rule-header {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__rule-name {
    font-weight: 600;
  }

  &__rule-content {
    font-size: 14px;
  }

  &__rule-description {
    color: #666;
    margin-bottom: 12px;
  }

  &__rule-condition,
  &__rule-filters,
  &__rule-notify {
    margin-bottom: 8px;

    strong {
      margin-right: 8px;
    }
  }

  &__rule-filters {
    .ant-tag {
      margin-top: 4px;
    }
  }

  &__rule-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
  }

  &__rule-status {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__rule-last-triggered {
    font-size: 12px;
    color: #999;
  }

  .text-muted {
    color: #999;
  }

  .text-small {
    font-size: 12px;
  }
}
</style>
